import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { Play, Pause, Star, ListPlus, User, Calendar, Eye, Clock, Disc, ChevronRight, Share2 } from 'lucide-react';
import { AudioFile } from '../types';
import { useAudioPlayer } from '../context/AudioPlayerContext';
import { useFavorites } from '../context/FavoritesContext';
import { useToast } from '../context/ToastContext';
import SEO from '../components/SEO';
import NotFound from '../components/NotFound';
import ShareModal from '../components/ShareModal';
import { generateNaatShareUrl } from '../utils/shareUtils';
import './NaatDetailsPage.css';

const BASE_DOMAIN = process.env.REACT_APP_BASE_DOMAIN || 'https://naatcity.com';

const NaatDetailsPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [naat, setNaat] = useState<AudioFile | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [is404Error, setIs404Error] = useState(false);
  const [isShareModalOpen, setIsShareModalOpen] = useState(false);

  const { state, playAudio, pauseAudio, addToQueue } = useAudioPlayer();
  const { favorites, addToFavorites, removeFromFavorites } = useFavorites();
  const { showToast } = useToast();

  const isPlaying = state.currentAudio?.id === naat?.id && state.isPlaying;
  const isFavorite = favorites.some(fav => fav.id === naat?.id);

  // Scroll to top when component mounts or ID changes
  useEffect(() => {
    // Scroll the actual scrollable containers based on index.css setup
    document.body.scrollTop = 0;

    const rootElement = document.getElementById('root');
    if (rootElement) {
      rootElement.scrollTop = 0;
    }

    // Also try with a small delay to ensure DOM is ready
    setTimeout(() => {
      document.body.scrollTop = 0;
      if (rootElement) {
        rootElement.scrollTop = 0;
      }
    }, 0);
  }, [id]);

  useEffect(() => {
    const fetchNaatDetails = async () => {
      if (!id) return;

      setLoading(true);
      setError(null);

      try {
        const url = `${BASE_DOMAIN}/api/audios/${id}`;

        const response = await fetch(url);
        if (!response.ok) {
          if (response.status === 404) {
            setIs404Error(true);
          }
          throw new Error(`Failed to fetch naat details: ${response.status} ${response.statusText}`);
        }

        const data = await response.json();

        if (data.success && data.data) {
          setNaat(data.data);
          setIs404Error(false);

          // Set document title for SEO
          const naatKhawanName = data.data.naatKhawan?.name || 'Unknown Artist';
          document.title = `${data.data.title} - ${naatKhawanName} : NaatCity`;
        } else {
          setIs404Error(true);
          throw new Error('Naat not found');
        }
      } catch (err) {
        setError(err instanceof Error ? err.message : 'An error occurred');
      } finally {
        setLoading(false);
      }
    };

    fetchNaatDetails();
  }, [id]);

  // Reset title when component unmounts
  useEffect(() => {
    return () => {
      document.title = 'NaatCity - Islamic Audio Streaming Platform';
    };
  }, []);

  const handlePlayPause = () => {
    if (!naat) return;

    if (isPlaying) {
      pauseAudio();
    } else {
      playAudio(naat, [], 'regular');
    }
  };

  const handleAddToQueue = () => {
    if (!naat) return;
    const added = addToQueue(naat);
    if (added) {
      showToast(`Added "${naat.title}" to queue`, 'success', 3000);
    } else {
      showToast(`"${naat.title}" is already in queue`, 'info', 3000);
    }
  };

  const handleToggleFavorite = () => {
    if (!naat) return;

    if (isFavorite) {
      removeFromFavorites(naat.id);
      showToast(`"${naat.title}" removed from favorites`, 'info', 3000);
    } else {
      addToFavorites(naat);
      showToast(`"${naat.title}" added to favorites`, 'success', 3000);
    }
  };

  const handleShare = () => {
    setIsShareModalOpen(true);
  };

  const handleNaatKhawanClick = () => {
    if (naat?.naatKhawan?.id) {
      navigate(`/naatkhawan/${naat.naatKhawan.id}`);
    }
  };

  const formatDuration = (seconds: number) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  if (loading) {
    return (
      <div className="nd-page">
        <div className="nd-loading-container">
          <div className="nd-loading-spinner"></div>
          <p>Loading Naat details...</p>
        </div>
      </div>
    );
  }

  if (error || !naat) {
    // Show the beautiful NotFound component for 404 errors
    if (is404Error) {
      return <NotFound type="naat" />;
    }

    // Show regular error for other types of errors
    return (
      <div className="nd-page">
        <div className="nd-error-container">
          <p className="nd-error-message">{error || 'Naat not found'}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="nd-page">
      <SEO
        title={`${naat.title} by ${naat.naatKhawan?.name || 'Unknown Artist'} - NaatCity`}
        description={`Listen to "${naat.title}" recited by ${naat.naatKhawan?.name || 'Unknown Artist'} on NaatCity. Stream this beautiful Islamic naat in high-quality audio. Duration: ${naat.duration ? formatDuration(naat.duration) : 'Unknown'}. Category: ${naat.category || 'Spiritual'}.`}
        keywords={`${naat.title}, ${naat.naatKhawan?.name}, islamic naat, ${naat.category || 'spiritual'} naat, urdu naat, arabic naat, listen online`}
        url={generateNaatShareUrl(naat.id)}
        type="music.song"
        audio={{
          title: naat.title,
          artist: naat.naatKhawan?.name || 'Unknown Artist',
          duration: naat.duration || 0
        }}
      />

      {/* Main Content Container */}
      <div className="nd-details-container">

        {/* Breadcrumb Navigation */}
        <div className="nd-breadcrumb">
          <Link to="/" className="nd-breadcrumb-link">Home</Link>
          <ChevronRight size={16} className="nd-breadcrumb-separator" />
          <Link to="/naatkhawans" className="nd-breadcrumb-link">Naat Khawans</Link>
          <ChevronRight size={16} className="nd-breadcrumb-separator" />
          <span className="nd-breadcrumb-current">
            {naat.naatKhawan?.name || 'Unknown Artist'}
          </span>
        </div>

        {/* Naat Information */}
        <div className="nd-info-section">
          <h1 className="nd-title">{naat.title}</h1>

          <button
            className="nd-khawan-link"
            onClick={handleNaatKhawanClick}
          >
            <User size={16} />
            <span>{naat.naatKhawan?.name || 'Unknown Artist'}</span>
          </button>

          {/* Modern Control Panel */}
          <div className={`nd-control-panel ${isPlaying ? 'nd-is-playing' : ''}`}>
            {/* Animated Album Art in Background */}
            {isPlaying && (
              <div className="nd-panel-album-art">
                <div className="nd-panel-visualizer">
                  <div className="nd-panel-bar"></div>
                  <div className="nd-panel-bar"></div>
                  <div className="nd-panel-bar"></div>
                  <div className="nd-panel-bar"></div>
                  <div className="nd-panel-bar"></div>
                  <div className="nd-panel-bar"></div>
                  <div className="nd-panel-bar"></div>
                  <div className="nd-panel-bar"></div>
                  <div className="nd-panel-bar"></div>
                  <div className="nd-panel-bar"></div>
                  <div className="nd-panel-bar"></div>
                  <div className="nd-panel-bar"></div>
                </div>
              </div>
            )}

            {/* Primary Play Control */}
            <div className="nd-primary-control">
              <button
                className={`nd-main-play-btn ${isPlaying ? 'nd-playing' : ''}`}
                onClick={handlePlayPause}
              >
                {isPlaying ? <Pause size={36} /> : <Play size={36} />}
                <span className="nd-play-text">
                  {isPlaying ? 'Now Playing' : 'Play Naat'}
                </span>
              </button>
            </div>

            {/* Secondary Controls */}
            <div className="nd-secondary-controls">
              <button
                className="nd-control-btn nd-queue-btn"
                onClick={handleAddToQueue}
                title="Add to Queue"
              >
                <ListPlus size={22} />
              </button>
              <button
                className={`nd-control-btn nd-favorite-btn ${isFavorite ? 'nd-active' : ''}`}
                onClick={handleToggleFavorite}
                title={isFavorite ? 'Remove from Favorites' : 'Add to Favorites'}
              >
                <Star size={22} fill={isFavorite ? 'currentColor' : 'none'} />
              </button>
              <button
                className="nd-control-btn nd-share-btn"
                onClick={handleShare}
                title="Share this Naat"
              >
                <Share2 size={22} />
              </button>
            </div>
          </div>

          {/* Metadata Grid */}
          <div className="nd-metadata-grid">
            {naat.category && (
              <div className="nd-metadata-item">
                <Disc />
                <div>
                  <span className="nd-label">Category</span>
                  <span className="nd-value">{naat.category}</span>
                </div>
              </div>
            )}

            {naat.duration && (
              <div className="nd-metadata-item">
                <Clock />
                <div>
                  <span className="nd-label">Duration</span>
                  <span className="nd-value">{formatDuration(naat.duration)}</span>
                </div>
              </div>
            )}

            <div className="nd-metadata-item">
              <Eye />
              <div>
                <span className="nd-label">Plays</span>
                <span className="nd-value">{naat.playCount || 0}</span>
              </div>
            </div>

            {naat.createdAt && (
              <div className="nd-metadata-item">
                <Calendar />
                <div>
                  <span className="nd-label">Added</span>
                  <span className="nd-value">{formatDate(naat.createdAt)}</span>
                </div>
              </div>
            )}
          </div>

          {/* Description if available */}
          {naat.description && (
            <div className="nd-description">
              <h3>About this Naat</h3>
              <p>{naat.description}</p>
            </div>
          )}
        </div>
      </div>

      {/* Share Modal */}
      <ShareModal
        isOpen={isShareModalOpen}
        onClose={() => setIsShareModalOpen(false)}
        title={naat.title}
        url={generateNaatShareUrl(naat.id)}
        description={naat.description}
        artist={naat.naatKhawan?.name}
        hashtags={[naat.category || 'Spiritual', naat.language]}
      />
    </div>
  );
};

export default NaatDetailsPage;